import React, { FC, useContext, useEffect, useState } from 'react';

import {
  Button,
  Input,
  Link,
  NumberInput,
  Pagination,
  SearchIcon,
  Tabs,
  Tooltip,
} from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import {
  AdvertisementManagerMobile,
  AdvertisementViewMobile,
} from 'components';
import { FeaturesContext } from 'features';
import {
  useGetAdvertisements,
  useGetMyAdvertisements,
  useGetPartnerAdvertisements,
  useWindowResize,
} from 'hooks';
import { LocalizationContext } from 'localization';
import {
  AdvertisementManagerModal,
  AdvertisementPublishedModal,
  AdvertisementViewModal,
} from 'modals';
import { ETheme, ThemeContext } from 'theme';
import { EAdvertisementStatus, EVerificationClubStatus } from 'types';

import s from './Advertisement.module.scss';
import { AllAdvertisement, MyAdvertisement } from './components';
import { AdvertisementInfo } from './components/AdvertisementInfo';
import { EAdvertisementActiveStatus } from './components/MyAdvertisement/interfaces';
import {
  EAdvertisementCategories,
  EAdvertisementItem,
  IModalAdvertisementManagerState,
  IModalAdvertisementViewState,
  IMyAdvertisementStatus,
} from './interfaces';

export const Advertisement: FC = () => {
  const { t } = useContext(LocalizationContext);
  const { theme } = useContext(ThemeContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);
  const { verification } = useContext(FeaturesContext);

  const [advertisementItem, setAdvertisementItem] = useState(
    EAdvertisementItem.PublicAdvertisement
  );
  const [advertisementCategories, setAdvertisementCategories] = useState(
    EAdvertisementCategories.ALL
  );
  const [advertisementStatus, setAdvertisementStatus] = useState(
    EAdvertisementActiveStatus.ACTIVE
  );

  const cardStatus = [
    {
      title: t('booking.list.tabs.active'),
      value: EAdvertisementStatus.ACTIVE,
      active: true,
    },
    {
      title: t('rejected'),
      value: EAdvertisementStatus.REJECTED,
      active: false,
    },
    {
      title: t('moderation'),
      value: EAdvertisementStatus.MODERATION,
      active: false,
    },
    {
      title: t('completed'),
      value: EAdvertisementStatus.COMPLETED,
      active: false,
    },
    {
      title: t('advertisement.expired'),
      value: EAdvertisementStatus.EXPIRED,
      active: false,
    },
  ];

  const [status, setStatus] = useState<IMyAdvertisementStatus[]>(cardStatus);

  const [searchStr, setSearchStr] = useState('');
  const [advertisementManagerState, setAdvertisementManagerState] =
    useState<IModalAdvertisementManagerState | null>(null);
  const [advertisementViewState, setAdvertisementViewState] =
    useState<IModalAdvertisementViewState | null>(null);

  const [openAdvertisementPublished, setOpenAdvertisementPublished] =
    useState(false);
  const [publicPage, setPublicPage] = useState(1);
  const [myPage, setMyPage] = useState(1);
  const [pageInput, setPageInput] = useState<number | ''>('');

  useEffect(() => {
    setPublicPage(1);
  }, [searchStr, advertisementCategories]);

  useEffect(() => {
    setMyPage(1);
  }, [searchStr, advertisementCategories]);

  useEffect(() => {
    setSearchStr('');
  }, [advertisementItem]);

  const handlePageChange = (page: number): void => {
    setSearchStr('');
    if (advertisementItem === EAdvertisementItem.PublicAdvertisement) {
      setPublicPage(page);
    } else {
      setMyPage(page);
    }
  };
  const handlePageInputSubmit = (): void => {
    const page = Number(pageInput);

    if (!page || Number.isNaN(page)) return;

    const safePage = Math.min(Math.max(page, 1), paginator?.lastPage || 1);

    handlePageChange(safePage);
    setPageInput('');
  };

  const ADVERTISEMENT_ITEM_OPTIONS = [
    {
      title: t('advertisement.tab.publish'),
      value: EAdvertisementItem.PublicAdvertisement,
    },
    {
      title: t('advertisement.tab.my'),
      value: EAdvertisementItem.MyAdvertisement,
    },
  ];

  const getAdvertisementCategory = (
    category: EAdvertisementCategories
  ): number | undefined => {
    switch (category) {
      case EAdvertisementCategories.ALL:
        return undefined;
      case EAdvertisementCategories.GOODS:
        return 1;
      case EAdvertisementCategories.SERVICES:
        return 2;
    }
  };

  const getAdvertisementStatus = (
    status: IMyAdvertisementStatus[]
  ): EAdvertisementStatus[] | undefined => {
    const result: EAdvertisementStatus[] = [];

    if (status.every((item) => !item.active))
      return [
        EAdvertisementStatus.EXPIRED,
        EAdvertisementStatus.COMPLETED,
        EAdvertisementStatus.MODERATION,
        EAdvertisementStatus.REJECTED,
      ];
    status.map((item) => {
      if (item.active) {
        result.push(item.value);
      }
    });
    return result;
  };

  const {
    advertisements: allAds,
    paginator: publicPaginator,
    loading: allLoading,
  } = useGetAdvertisements({
    page: publicPage,
    first: 60,
    skip: advertisementItem === EAdvertisementItem.MyAdvertisement,
    filter: {
      query: searchStr.length > 3 ? searchStr : undefined,
      category_id: getAdvertisementCategory(advertisementCategories),
    },
  });

  const shouldLoadPartners =
    advertisementItem === EAdvertisementItem.PublicAdvertisement &&
    publicPage === 1 &&
    (!searchStr || searchStr.length < 4) &&
    advertisementCategories === EAdvertisementCategories.ALL;

  const { partners, loading: partnersLoading } = useGetPartnerAdvertisements(
    !shouldLoadPartners
  );

  const {
    advertisements: myAds,
    paginator: myPaginator,
    loading: myLoading,
  } = useGetMyAdvertisements({
    page: myPage,
    first: 30,
    skip: advertisementItem === EAdvertisementItem.PublicAdvertisement,
    status: getAdvertisementStatus(status),
    query: searchStr.length > 3 ? searchStr : undefined,
  });

  const paginator =
    advertisementItem === EAdvertisementItem.PublicAdvertisement
      ? publicPaginator
      : myPaginator;

  const size = useWindowResize();

  if (size.width <= 960 && advertisementViewState) {
    return (
      <AdvertisementViewMobile
        onClose={() => setAdvertisementViewState(null)}
        mode={advertisementViewState?.mode}
        advertisement={advertisementViewState?.advertisement}
        setAdvertisementManagerState={setAdvertisementManagerState}
        setAdvertisementViewState={setAdvertisementViewState}
      />
    );
  }

  if (size.width <= 960 && advertisementManagerState) {
    return (
      <AdvertisementManagerMobile
        onClose={() => setAdvertisementManagerState(null)}
        setOpenAdvertisementPublished={setOpenAdvertisementPublished}
        mode={advertisementManagerState?.mode}
        advertisement={advertisementManagerState?.advertisement}
      />
    );
  }

  return (
    <>
      <AdvertisementInfo />
      <div className={s.demoNotice} role="note">
        Все объявления в демо вымышлены и показаны только для примера. Контакты
        продавцов не предназначены для связи или совершения сделок.
      </div>
      <div className={s.container}>
        {advertisementViewState && size.width > 960 && (
          <AdvertisementViewModal
            onClose={() => setAdvertisementViewState(null)}
            mode={advertisementViewState?.mode}
            advertisement={advertisementViewState?.advertisement}
            setAdvertisementManagerState={setAdvertisementManagerState}
            setAdvertisementViewState={setAdvertisementViewState}
          />
        )}

        {!advertisementViewState && advertisementManagerState && (
          <AdvertisementManagerModal
            onClose={() => setAdvertisementManagerState(null)}
            setOpenAdvertisementPublished={setOpenAdvertisementPublished}
            mode={advertisementManagerState?.mode}
            advertisement={advertisementManagerState?.advertisement}
          />
        )}
        {openAdvertisementPublished && (
          <AdvertisementPublishedModal
            onClose={() => setOpenAdvertisementPublished(false)}
            setAdvertisementItem={setAdvertisementItem}
          />
        )}
        <div className={s.header}>
          <div className={s.left}>
            {permissions?.has('advertisement_manage') && (
              <Tabs
                options={ADVERTISEMENT_ITEM_OPTIONS}
                onChange={(value) => setAdvertisementItem(value)}
                value={advertisementItem}
                className={s.tabs}
              />
            )}
            <Input
              variant="secondary"
              value={searchStr}
              onChange={setSearchStr}
              placeholder={t('advertisement.search')}
              leftContent={<SearchIcon color="primary" />}
              className={s.search}
              errorWrapperClassName={s.searchInput}
            />
          </div>
          {permissions?.has('advertisement_manage') && (
            <div className={s.right}>
              {verification?.status !== EVerificationClubStatus.VERIFIED ? (
                <Tooltip
                  content={t('advertisement.button.disabled')}
                  contentClassName={s.tooltip}
                  className={s.tooltip}
                  position="bottom-right"
                  footer={
                    <Link
                      title={t('settings.lock.action')}
                      link={'https://lk.smartshell.gg'}
                    />
                  }
                >
                  <Button
                    iconName="CirclePlusIcon"
                    onClick={() =>
                      setAdvertisementManagerState({
                        mode: 'create',
                      })
                    }
                    className={s.add_button}
                    disabled={true}
                  >
                    {t('advertisement.button.add')}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  iconName="CirclePlusIcon"
                  onClick={() =>
                    setAdvertisementManagerState({
                      mode: 'create',
                    })
                  }
                  className={s.add_button}
                  disabled={!permissions?.has('advertisement_manage')}
                >
                  {t('advertisement.button.add')}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className={s.main}>
          {advertisementItem === EAdvertisementItem.PublicAdvertisement && (
            <AllAdvertisement
              allAds={allAds}
              partners={partners}
              loading={allLoading || partnersLoading}
              advertisementCategories={advertisementCategories}
              setAdvertisementCategories={setAdvertisementCategories}
              setAdvertisementViewState={setAdvertisementViewState}
              searchStr={searchStr}
            />
          )}

          {advertisementItem === EAdvertisementItem.MyAdvertisement && (
            <MyAdvertisement
              loading={myLoading}
              data={myAds}
              status={status}
              setStatus={setStatus}
              setAdvertisementManagerState={setAdvertisementManagerState}
              setAdvertisementViewState={setAdvertisementViewState}
              advertisementStatus={advertisementStatus}
              setAdvertisementStatus={setAdvertisementStatus}
            />
          )}
          {searchStr === '' &&
            !(allLoading || myLoading) &&
            ((!allAds.length &&
              advertisementItem === EAdvertisementItem.PublicAdvertisement &&
              !partners?.length) ||
              (!myAds.length &&
                advertisementItem === EAdvertisementItem.MyAdvertisement)) && (
              <div className={s.placeholder}>
                <img
                  className={s.image}
                  src={`/images/Advertisement/advertisementEmpty${
                    theme === ETheme.DARK ? 'Dark' : 'White'
                  }.png`}
                  alt="placeholder_image"
                />
                <span className={s.text}>
                  {t('advertisement.placeholder.text')}
                </span>
              </div>
            )}
          {searchStr !== '' &&
            !(allLoading || myLoading) &&
            ((!allAds.length &&
              advertisementItem === EAdvertisementItem.PublicAdvertisement &&
              !partners?.length) ||
              (!myAds.length &&
                advertisementItem === EAdvertisementItem.MyAdvertisement)) && (
              <div className={s.placeholder}>
                <img
                  className={s.image}
                  src={`/images/Advertisement/advertisementSearch${
                    theme === ETheme.DARK ? 'Dark' : 'White'
                  }.png`}
                  alt="placeholder_image"
                />
                <span className={s.text}>
                  {t('advertisement.placeholder.search')}
                </span>
              </div>
            )}
        </div>
        {paginator && paginator?.lastPage > 1 && (
          <div className={s.pagination_bottom}>
            <Pagination
              currentPage={paginator.currentPage}
              lastPage={paginator.lastPage}
              onChange={handlePageChange}
              className={s.pagination}
              isChose
            />
            {paginator?.lastPage > 1 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePageInputSubmit();
                }}
              >
                <NumberInput
                  value={pageInput}
                  onChange={(val) => {
                    if (typeof val === 'number') {
                      setPageInput(val);
                    } else {
                      setPageInput('');
                    }
                  }}
                  onBlur={(e) => {
                    e.preventDefault();
                    handlePageInputSubmit();
                  }}
                  placeholder="№"
                  variant="secondary"
                  className={s.pagination_input}
                />
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
};
